/**
 * Wall Create Tool — "create walls"
 *
 * Two modes, dispatched on whether the active floor already has a (non-empty)
 * wall sketch:
 *
 *  - SUGGESTION mode (rooms drawn, no wall sketch): the OutlineTool flow —
 *    click rooms to include, live green outer-wall preview, thickness setting,
 *    Enter commits — plus a debounced live preview of the wall sketch that
 *    accepting would generate. Enter creates the OUTLINE annotation(s) AND
 *    generates + persists the floor's wall sketch. The "outer wall only"
 *    setting skips sketch generation (the classic outline-only workflow).
 *    Escape leaves the suggestion flow for DRAW mode (until re-enable).
 *
 *  - DRAW mode (wall sketch exists, the floor has no rooms to suggest from,
 *    or the user escaped the suggestion): wall-creating gestures only — drag
 *    in empty space to draw a wall, drag from a node/edge to branch, or
 *    click-click: first click anchors the start, second click commits the
 *    segment (both clicks snap, so clicking near existing geometry chains).
 *    On a floor without a sketch the first press creates an EMPTY sketch and
 *    the drag/click becomes its first wall. No selection of any kind;
 *    right-click deletes the hovered node/wall (or cancels a pending
 *    click-click segment). Enter commits, Esc cancels the drawing session.
 *
 * Part of the wall mode in the floorplan persona.
 */

import * as THREE from 'three';
import { Viewer } from '../../../viewer/viewer';
import { IPersona, IViewConfig } from '../../types';
import { ViewModes } from '../../helpers';
import { ToolSettingsSchema, ToolSettingsChangeEvent } from '../../ToolSettings';
import { OutlineTool } from './OutlineTool';
import { WallSketchEditSession } from './WallSketchEditSession';
import { DragEventData } from '../../BaseTool';
import { PointoramaWallSketch, WallRegionData } from '../../../utils/PointoramaWallSketch';
import { WallRegionComponent } from '../../../utils/WallRegionBuilder';
import { FloorSkeleton } from '../../../utils/SkeletonProcessingPipeline';
import { generateProcessedSkeleton, generateAndPersistWallSketch, getSharedSkeletonPipeline, maybeConvertLegacyOutline, writeOutlineRingsToSketch, RoomPolygonData } from '../../../utils/skeleton/buildLayoutWallSketch';
import type { OutlineRingComponent } from '../../../utils/skeleton/outlineRings';
import { WallSketchPreviewRenderer } from '../../../utils/skeleton/WallSketchPreviewRenderer';
import { SkeletonDebugWidget, SKELETON_PRUNE_MATCH_DEBUG_GROUP_NAME, SKELETON_CORNER_MATCH_DEBUG_GROUP_NAME, SKELETON_PAIR_WALLS_MATCH_INF_DEBUG_GROUP_NAME } from '../widgets/SkeletonDebugWidget';
import { applyFixtureJsonToWallSketch, downloadSkeletonFixture } from '../../../utils/skeleton/skeletonFixture';
import { createDebugGroup, addRayToDebugGroup } from '../../../utils/DebugVisualization';
import { getFloorWallSourceProperty, hasGlobalPropertyService } from '@pointorama/potree-shared';
import { t } from '../../../translation/markers';

/** Trailing-edge debounce for the sketch preview. The generation pipeline runs
 * on the main thread — this keeps rapid room-toggling responsive. */
const SKETCH_PREVIEW_DEBOUNCE_MS = 300;

export class WallCreateTool extends OutlineTool {
  override readonly name: string = 'wall_create';

  /** "Outer wall only": commit creates just the outline(s), no wall sketch. */
  private outlineOnly = false;

  // Draw-mode interaction engine — creation gestures on, selection off.
  private readonly session: WallSketchEditSession;

  // Suggestion-mode sketch preview state.
  private readonly previewRenderer: WallSketchPreviewRenderer;
  private previewTimer: number | null = null;
  private previewAbort: AbortController | null = null;
  /** Monotonic token — bumped on every schedule/cancel so stale async
   * completions (aborted or slow) can never render over a newer preview. */
  private previewToken = 0;
  /** Last completed preview, keyed by room-selection + thickness, so Enter
   * right after the preview settles reuses it instead of regenerating. */
  private cachedPreview: { key: string; skeleton: FloorSkeleton } | null = null;
  /** Subscription to the current floor's `floor_wall_source` property — the
   * preview's outline-only filter follows it, so a source change while the
   * suggestion is on screen must re-render. */
  private wallSourceSubscription: { unsubscribe(): void } | null = null;
  private subscribedWallSourceFloorId: string | null = null;

  /** Pinned into DRAW mode: set on Escape from the suggestion flow and on the
   * first press of a sketchless floor, so rooms appearing (or a room landing
   * mid-gesture) can't flip the tool back to suggestion mode. Reset on
   * enable/floor change — the suggestion returns only then. */
  private manualDrawArmed = false;
  /** Drag-start buffered while the empty sketch requested in onMouseDown is
   * still being created; replayed as the first wall when it arrives. */
  private pendingDrawDragStart: DragEventData | null = null;
  /** One `request_skeleton` per enablement/floor — the React handler is
   * idempotent, this just avoids re-dispatch spam while creation is inflight. */
  private emptySketchRequested = false;

  /** Debug window: the skeleton pipeline pass panel + fixture export/load.
   * Wired to the SHARED pipeline (the one the preview + commit run through),
   * so editing a pass re-runs the live preview with the change applied. */
  private debugWidget: SkeletonDebugWidget | null = null;

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);
    this.initUndoRedo();
    this.previewRenderer = new WallSketchPreviewRenderer(viewer);
    this.session = new WallSketchEditSession({
      viewer: this.viewer,
      getSceneWallSketch: () => this.getCurrentFloorWallSketch(),
      getUndoRedo: () => this.undoRedo,
      dispatchPhase: () => this.dispatchPhase(),
      capabilities: { allowCreate: true, allowSelect: false },
    });
  }

  // ─── Mode dispatch ──────────────────────────────────────────────────

  private getCurrentFloorWallSketch(): PointoramaWallSketch | null {
    const groupId = this.currentFloorGroupIdentifier;
    if (!groupId) return null;
    return this.viewer.scene.wallSketches.find((s) => s.groupIdentifier === groupId) ?? null;
  }

  /** Any rooms on the active floor — without them there is nothing for the
   * suggestion flow to work from, so the tool goes straight to drawing. */
  private floorHasRooms(): boolean {
    const groupId = this.currentFloorGroupIdentifier;
    if (!groupId) return false;
    return this.viewer.scene.rooms.some((r) => r.groupIdentifier === groupId);
  }

  /**
   * DRAW mode is active when the floor has a wall sketch with actual content,
   * when a drawing session is in progress on an EMPTY sketch, when the user
   * pinned it (first press on a sketchless floor / Escape from the
   * suggestion), or when the floor has no rooms to suggest from. A bare EMPTY
   * sketch annotation alone does not count — the user still gets the
   * suggestion flow to seed it.
   */
  private isDrawMode(): boolean {
    const sketch = this.getCurrentFloorWallSketch();
    if (sketch && sketch.getNodes().length > 0) return true;
    return this.session.isEditing || this.manualDrawArmed || !this.floorHasRooms();
  }

  /** Quest phases: draw mode reports draw/drawing; suggestion mode falls back
   * to the OutlineTool's idle/has_selection pair. */
  protected override dispatchPhase(): void {
    if (this.isDrawMode()) {
      this.dispatchStateChanged({ phase: this.session.isEditing ? 'drawing' : 'draw' });
      return;
    }
    super.dispatchPhase();
  }

  // Every wall-tab tool offers the Plan view; extend OutlineTool's list.
  override getViewConfig(): IViewConfig {
    return {
      views: [ViewModes.XRAY_2D, ViewModes.PLAN_2D_SKELETON, ViewModes.ORTHOGRAPHIC_2D_TOP, ViewModes.PERSPECTIVE_3D_RGB],
      defaultViewId: ViewModes.XRAY_2D.id,
    };
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────

  protected override onEnable(): void {
    super.onEnable();
    this.manualDrawArmed = false;
    this.pendingDrawDragStart = null;
    this.emptySketchRequested = false;
    this.maybeConvertLegacyOutlineForCurrentFloor();
    this.setWallSketchSphereScale(1.2);
    // Selection may have been left on the sketch by another tool (the shared
    // WallSketchSelection lives on the sketch, not the tool) — this tool has
    // no selection, so drop any stale entries and their visuals.
    this.clearSketchSelection();
    // A sketch landing mid-session (our own commit, or a sync) flips us into
    // draw mode; keep the phase + previews in step.
    this.viewer.scene.addEventListener('wall_sketch_added', this.handleWallSketchAdded);
    this.syncWallSourceSubscription();

    // Debug window: pipeline pass panel + fixture export/load. The pipeline
    // tab edits the shared instance the preview/commit actually run through.
    if (this.viewer.debugOverlay) {
      if (!this.debugWidget) {
        this.debugWidget = new SkeletonDebugWidget(this.viewer, {
          onRegenerateWallSketch: () => this.rerunSketchPreview(),
          onRunPass: () => this.rerunSketchPreview(),
          onExportFixture: () => this.exportSketchFixture(),
          onLoadFixture: (json) => this.loadSketchFixture(json),
        });
        this.debugWidget.setPipeline(getSharedSkeletonPipeline());
      }
      this.viewer.debugOverlay.addWidget(this.debugWidget);
    }

    this.dispatchPhase();
  }

  protected override onDisable(): void {
    this.viewer.scene.removeEventListener('wall_sketch_added', this.handleWallSketchAdded);
    this.wallSourceSubscription?.unsubscribe();
    this.wallSourceSubscription = null;
    this.subscribedWallSourceFloorId = null;
    this.pendingDrawDragStart = null;
    // Auto-confirm in-progress drawing so the user's walls persist when they
    // switch tools mid-session (mirrors the wall sketch editor).
    if (this.session.isEditing) {
      this.session.commit();
    }
    // Same keep-the-work policy for the suggestion flow: an active preview
    // (rooms selected) is committed into the sketch object instead of being
    // silently dropped. commit() no-ops when nothing is selected.
    if (!this.isDrawMode()) {
      this.commit();
    }
    // Hover-only helpers (the branch-point preview marker) can exist without
    // an edit session — tear them down explicitly.
    this.session.releaseHoverHelpers();
    this.cancelSketchPreview();
    this.previewRenderer.dispose();
    this.cachedPreview = null;
    if (this.viewer.debugOverlay && this.debugWidget) {
      try {
        this.viewer.debugOverlay.removeWidget(this.debugWidget.name);
      } catch {
        // Widget may already be removed
      }
    }
    this.setWallSketchSphereScale(0.6);
    super.onDisable();
  }

  protected override onFloorChanged(): void {
    // Abandon any drawing session on the previous floor (commit — same
    // keep-the-work policy as tool disable), then re-evaluate mode.
    if (this.session.isEditing) {
      this.session.commit();
    }
    this.session.releaseHoverHelpers();
    this.cachedPreview = null;
    // Each floor gets its own suggestion evaluation — un-pin draw mode and
    // drop any inflight empty-sketch bootstrap from the previous floor.
    this.manualDrawArmed = false;
    this.pendingDrawDragStart = null;
    this.emptySketchRequested = false;
    this.maybeConvertLegacyOutlineForCurrentFloor();
    this.syncWallSourceSubscription();
    super.onFloorChanged(); // releases hover + refreshPreview (mode-aware, below)
    this.dispatchPhase();
  }

  /** (Re)subscribe to the active floor's `floor_wall_source` property so the
   * suggestion preview follows the Source dropdown live: Measured shows only
   * the outer walls, Standardised the full skeleton. */
  private syncWallSourceSubscription(): void {
    const floorId = this.currentFloorGroupIdentifier;
    if (floorId === this.subscribedWallSourceFloorId && this.wallSourceSubscription) return;
    this.wallSourceSubscription?.unsubscribe();
    this.wallSourceSubscription = null;
    this.subscribedWallSourceFloorId = floorId;
    if (!floorId || !hasGlobalPropertyService()) return;
    try {
      this.wallSourceSubscription = getFloorWallSourceProperty(floorId).subscribeToChanges(this.handleWallSourceChanged);
    } catch {
      /* property service not ready */
    }
  }

  private readonly handleWallSourceChanged = (): void => {
    // The generated skeleton is source-independent — only the outer-walls-only
    // render filter changed. Re-render the settled preview directly when its
    // inputs still match; otherwise fall back to the normal refresh funnel.
    const floorPlane = this.currentFloorFloorPlane;
    if (!this.isDrawMode() && !this.outlineOnly && floorPlane && this.cachedPreview) {
      const rooms = this.getSelectedRooms().map((r) => ({ identifier: r.identifier, polygon: r.getPolygon() }));
      if (this.cachedPreview.key === this.previewKey(rooms)) {
        const outerWallsOnly = this.viewer.floorplanRepresentation?.isOriginalActive(this.currentFloorGroupIdentifier) ?? false;
        this.previewRenderer.setSkeleton(this.cachedPreview.skeleton, floorPlane.height, { outerWallsOnly });
        return;
      }
    }
    this.refreshPreview();
  };

  /** Measured floors fold their legacy OUTLINE annotation into the sketch as
   *  merged ring edges before any drawing happens on them — so a draw gesture
   *  EXTENDS the outline instead of starting a parallel one. One undoable
   *  step (see WallSketchEditorTool's counterpart). */
  private maybeConvertLegacyOutlineForCurrentFloor(): void {
    const floorId = this.currentFloorGroupIdentifier;
    if (!floorId) return;
    maybeConvertLegacyOutline(this.viewer, floorId, this.currentFloorFloorPlane?.height ?? 0, {
      onBeforeConvert: () => {
        if (this.getCurrentFloorWallSketch()) this.session.pushWallSketchUndoSnapshot();
      },
    });
  }

  private readonly handleWallSketchAdded = (event: { skeleton?: PointoramaWallSketch }): void => {
    const groupId = this.currentFloorGroupIdentifier;
    if (!event.skeleton || !groupId || event.skeleton.groupIdentifier !== groupId) return;
    // A drag buffered while the empty-sketch bootstrap was inflight becomes
    // the first wall the moment the sketch lands (startDrawWall works on an
    // empty sketch by contract). Synchronous — the user is mid-drag.
    const pending = this.pendingDrawDragStart;
    if (pending) {
      this.pendingDrawDragStart = null;
      this.session.startDrawWall(pending);
    }
    // Defer one tick: the sketch lands EMPTY and is populated by a
    // later-registered listener of this same `wall_sketch_added` dispatch
    // (see generateAndPersistWallSketch) — re-checking immediately would
    // still read "no nodes" and miss the flip into draw mode.
    window.setTimeout(() => {
      if (!this.enabled) return;
      this.refreshPreview();
      this.dispatchPhase();
    }, 0);
  };

  /** Ask the React layer to create an EMPTY wall sketch for the current floor
   *  (`request_skeleton` with no rooms — the same channel the layout bootstrap
   *  and the wall sketch editor use). `handleWallSketchAdded` replays any
   *  buffered drag when it arrives. */
  private requestEmptySketch(): void {
    const groupId = this.currentFloorGroupIdentifier;
    if (!groupId || this.emptySketchRequested) return;
    this.emptySketchRequested = true;
    this.viewer.scene.dispatchEvent({
      type: 'request_skeleton',
      value: {
        floorGroupIdentifier: groupId,
        floorHeight: this.currentFloorFloorPlane?.height ?? 0,
        roomIdentifiers: [],
      },
    });
  }

  private setWallSketchSphereScale(scale: number): void {
    this.viewer.scene.wallSketchSphereScale = scale;
    for (const sketch of this.viewer.scene.wallSketches) {
      for (const sphere of sketch.spheres) sphere.setScaleFactor(scale);
    }
  }

  private clearSketchSelection(): void {
    // clearSilent also tears down the highlight visuals.
    this.getCurrentFloorWallSketch()?.getSelection().clearSilent();
  }

  // ─── Settings ───────────────────────────────────────────────────────

  override getSettingsSchema(): ToolSettingsSchema {
    return {
      ...super.getSettingsSchema(),
      outlineOnly: {
        type: 'toggle',
        label: t('Outer wall only'),
        description: 'Only create the outer wall; skip generating the wall sketch',
        defaultValue: this.outlineOnly,
      },
    };
  }

  override onSettingsChange(event: ToolSettingsChangeEvent): void {
    super.onSettingsChange(event); // wallThickness → refreshPreview (mode-aware)
    if (event.key === 'outlineOnly') {
      this.outlineOnly = event.value as boolean;
      if (this.outlineOnly) {
        this.cancelSketchPreview();
        this.previewRenderer.clear();
      } else {
        this.scheduleSketchPreview();
      }
    }
  }

  // ─── Input routing (mode-dependent) ─────────────────────────────────

  /** Wrap a bare mouse event as DragEventData so the click-click draw flow
   * can drive the session's drag machinery (world coords unused there). */
  private dragDataFromMouseEvent(event: MouseEvent): DragEventData {
    const screen = new THREE.Vector2(event.clientX, event.clientY);
    return { button: event.button, startScreen: screen.clone(), currentScreen: screen, startWorld: null, currentWorld: null, event };
  }

  public override onMouseMove(event: MouseEvent): void {
    if (!this.isDrawMode()) {
      super.onMouseMove(event);
      return;
    }
    // Click-click draw in flight: the cursor drives the wall-end preview
    // (same live preview + snapping as mid-drag).
    if (this.session.isDrawingWall) {
      this.session.onDrag(this.dragDataFromMouseEvent(event));
      this.viewer.renderer.domElement.style.cursor = 'crosshair';
      return;
    }
    this.session.updateHover(event);
    // Pointer over a deletable/extrudable target, crosshair elsewhere (the
    // draw cursor).
    this.viewer.renderer.domElement.style.cursor = this.session.hasHoverTarget() ? 'pointer' : 'crosshair';
  }

  public onMouseDown(event: MouseEvent): void {
    if (!this.isDrawMode()) return;
    if (event.button === 1) return; // middle-click is reserved for camera pan
    // Sketchless floor: pin draw mode (a room landing mid-gesture must not
    // flip us back to suggestion) and kick the async empty-sketch creation so
    // the drag that may follow can become the first wall.
    if (event.button === 0 && !this.getCurrentFloorWallSketch()) {
      this.manualDrawArmed = true;
      this.requestEmptySketch();
    }
    this.session.onMouseDown(event);
  }

  public override onClick(event: MouseEvent): void {
    if (!this.isDrawMode()) {
      super.onClick(event); // room include/exclude toggling
      return;
    }
    if (event.button !== 0) return;
    // Click-click drawing: the drag gesture's press/release split across two
    // clicks. The first click anchors the wall start, the second click
    // commits the segment through the exact drag-release path. Drags keep
    // working unchanged (BaseTool only reports a click below its drag
    // threshold).
    const data = this.dragDataFromMouseEvent(event);
    if (this.session.isDrawingWall) {
      this.session.onDragEnd(data); // second click: commit at the click point
      return;
    }
    // First click. On a still-sketchless floor the empty-sketch creation
    // kicked in onMouseDown is inflight — buffer the anchor; it becomes the
    // draw start the moment the sketch lands (same as the drag flow).
    if (!this.getCurrentFloorWallSketch()) {
      this.pendingDrawDragStart = data;
      return;
    }
    // Route through the session's drag-start so a click gets the same gesture
    // a press would: empty space → draw-wall, on a node/edge → the extrude
    // gestures — with their full snapping (parallel/perpendicular guides).
    this.session.onDragStart(data);
  }

  public onRightClick(event: MouseEvent): void {
    if (!this.isDrawMode()) return;
    // Right-click during a pending click-click draw cancels it (the session's
    // mismatched-button release path, which also swallows the delete gesture).
    if (this.session.isDrawingWall) {
      this.session.onDragEnd(this.dragDataFromMouseEvent(event));
      return;
    }
    this.session.onRightClick(event); // deletes the hovered node/wall
  }

  public onDragStart(data: DragEventData): void {
    if (!this.isDrawMode()) return;
    if (data.button === 1) return; // middle-button drag is camera pan
    // Click-click draw in flight: the draw-wall drag already exists — don't
    // let onDragStart open a competing gesture (extrude/box-select). onDrag
    // keeps updating the preview and the release commits via onDragEnd.
    if (this.session.isDrawingWall) return;
    // Sketch creation (kicked in onMouseDown) still in flight — buffer this
    // drag start; it becomes the first wall the moment the sketch lands.
    if (data.button === 0 && !this.getCurrentFloorWallSketch()) {
      this.pendingDrawDragStart = data;
      return;
    }
    this.session.onDragStart(data);
  }

  public onDrag(data: DragEventData): void {
    if (!this.isDrawMode()) return;
    if (data.button === 1) return;
    this.session.onDrag(data);
  }

  public onDragEnd(data: DragEventData): void {
    if (!this.isDrawMode()) return;
    if (data.button === 1) return;
    // Released before the sketch creation landed — nothing to draw.
    if (this.pendingDrawDragStart) {
      this.pendingDrawDragStart = null;
      return;
    }
    this.session.onDragEnd(data);
  }

  public override onKeyDown(event: KeyboardEvent): void {
    // Let focused text/number inputs own the keyboard (both modes).
    const target = event.target as HTMLElement | null;
    const active = (target ?? document.activeElement) as HTMLElement | null;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) {
      return;
    }

    if (!this.isDrawMode()) {
      // Escape leaves the suggestion flow for manual drawing; everything else
      // (Enter → commit, …) stays OutlineTool behavior.
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.enterManualDraw();
        return;
      }
      super.onKeyDown(event);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.session.cancel();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.session.commit();
    }
  }

  /** Escape from the suggestion flow: pin DRAW mode, drop every suggestion
   *  preview and clear the room selection. The `measurement_select`
   *  round-trip re-runs refreshPreview + dispatchPhase, which now (draw mode)
   *  clear the outline preview and report the draw phase. */
  private enterManualDraw(): void {
    this.manualDrawArmed = true;
    this.cancelSketchPreview();
    this.previewRenderer.clear();
    this.viewer.scene.dispatchEvent({
      type: 'measurement_select',
      value: { measure: null, isCtrl: false },
    });
  }

  // ─── Suggestion-mode previews ───────────────────────────────────────

  /** Mode-aware preview refresh: draw mode shows no suggestion previews at
   * all; suggestion mode renders the outline preview (super) and schedules
   * the debounced sketch preview on top. */
  protected override refreshPreview(): void {
    if (this.isDrawMode()) {
      this.clearPreview();
      this.lastComponents = null;
      this.cancelSketchPreview();
      this.previewRenderer.clear();
      return;
    }
    super.refreshPreview();
    this.scheduleSketchPreview();
  }

  /** Cache key for the current suggestion inputs — room set + thickness. */
  private previewKey(rooms: RoomPolygonData[]): string {
    return `${rooms
      .map((r) => r.identifier)
      .sort()
      .join(',')}|${this.wallThickness.toFixed(3)}`;
  }

  private scheduleSketchPreview(): void {
    this.cancelSketchPreview();
    if (this.outlineOnly) {
      this.previewRenderer.clear();
      return;
    }
    const components = this.lastComponents;
    const floorPlane = this.currentFloorFloorPlane;
    if (!components || !floorPlane) {
      this.previewRenderer.clear();
      return;
    }
    const rooms = this.getSelectedRooms().map((r) => ({ identifier: r.identifier, polygon: r.getPolygon() }));
    const floorHeight = floorPlane.height;
    this.previewTimer = window.setTimeout(() => {
      this.previewTimer = null;
      void this.runSketchPreview(rooms, components, floorHeight);
    }, SKETCH_PREVIEW_DEBOUNCE_MS);
  }

  private async runSketchPreview(rooms: RoomPolygonData[], components: WallRegionComponent[], floorHeight: number): Promise<void> {
    const token = ++this.previewToken;
    const abort = new AbortController();
    this.previewAbort = abort;
    try {
      const processed = await generateProcessedSkeleton(rooms, floorHeight, components, abort.signal);
      if (token !== this.previewToken) return; // superseded or cancelled
      this.cachedPreview = { key: this.previewKey(rooms), skeleton: processed };
      // Measured-source floors show only the outline in their representation
      // — mirror that in the preview (outer walls only). The FULL skeleton
      // is still generated, cached and committed on accept.
      const outerWallsOnly = this.viewer.floorplanRepresentation?.isOriginalActive(this.currentFloorGroupIdentifier) ?? false;
      this.previewRenderer.setSkeleton(processed, floorHeight, { outerWallsOnly });
      // The debug window's pipeline tab shows per-pass timings of this run.
      this.debugWidget?.updateTimings();
      this.renderPruneMatchTies(processed, floorHeight);
    } catch (err) {
      if (!abort.signal.aborted) {
        console.error('Wall sketch preview generation failed:', err);
      }
    } finally {
      if (this.previewAbort === abort) this.previewAbort = null;
    }
  }

  private cancelSketchPreview(): void {
    if (this.previewTimer !== null) {
      window.clearTimeout(this.previewTimer);
      this.previewTimer = null;
    }
    this.previewAbort?.abort();
    this.previewAbort = null;
    this.previewToken++;
  }

  // ─── Debug window (pipeline + fixtures) ─────────────────────────────

  /** Pipeline edited (pass toggled/reordered/re-parameterized) or explicit
   * regenerate: drop the settled preview — its skeleton came from the OLD
   * stack — and re-run through the normal refresh funnel. A no-op outside
   * the suggestion flow (draw mode has no generated preview to re-run). */
  private rerunSketchPreview(): void {
    this.cachedPreview = null;
    this.refreshPreview();
  }

  /** Render the prune pass's matching ties (green = locked, red = rejected)
   * from this preview run — the create tool's counterpart of the editor's
   * debugRays routing, so the debug menu's "Show Prune Matches" toggle works
   * during the live suggestion preview. The group is recreated per run so
   * ties always reflect the latest pipeline result; the checkbox drives its
   * visibility. */
  private renderPruneMatchTies(skeleton: FloorSkeleton, floorHeight: number): void {
    const pruneGroup = createDebugGroup(this.viewer, SKELETON_PRUNE_MATCH_DEBUG_GROUP_NAME);
    const cornerGroup = createDebugGroup(this.viewer, SKELETON_CORNER_MATCH_DEBUG_GROUP_NAME);
    const pairGroup = createDebugGroup(this.viewer, SKELETON_PAIR_WALLS_MATCH_INF_DEBUG_GROUP_NAME);
    for (const ray of skeleton.debugRays ?? []) {
      const origin = new THREE.Vector3(ray.origin.x, ray.origin.y, floorHeight + 0.02);
      if (ray.label === 'prune-match') {
        addRayToDebugGroup(pruneGroup, origin, ray.direction, ray.distance, ray.hit, { hitColor: 0x00ff00, missColor: 0xff0000, opacity: 0.8 });
      } else if (ray.label === 'corner-match') {
        addRayToDebugGroup(cornerGroup, origin, ray.direction, ray.distance, ray.hit, { hitColor: 0xffcc00, missColor: 0xff0000, opacity: 0.9 });
      } else if (ray.label === 'pair-walls-match-inf' || ray.label === 'pair-walls-extend') {
        // Wall-pairing probes (midline discovery in the centering passes):
        // green = found a facing wall, red = probe missed. Toggled by the
        // debug menu's "Show Pair Walls Match Inf".
        addRayToDebugGroup(pairGroup, origin, ray.direction, ray.distance, ray.hit, { hitColor: 0x00ff00, missColor: 0xff0000, opacity: 0.6 });
      }
    }
  }

  /** Same unified fixture as the editor's export: the floor sketch's nodes /
   * edges / regions / geometry, the room+outline polygons (from the settled
   * preview when available), and the shared pipeline stack. */
  private exportSketchFixture(): void {
    const sketch = this.getCurrentFloorWallSketch();
    if (!sketch) {
      console.warn('Export skeleton fixture: no wall sketch on the current floor');
      return;
    }
    const roomConnectivity = this.cachedPreview?.skeleton.roomConnectivity?.map((r) => ({
      identifier: r.identifier,
      polygon: r.polygon.map((p) => ({ x: p.x, y: p.y })),
    }));
    const fixture = sketch.captureFixture(`floor ${this.currentFloorGroupIdentifier ?? 'unknown'}`, {
      roomConnectivity,
      pipelineConfig: getSharedSkeletonPipeline().getConfig(),
    });
    downloadSkeletonFixture(fixture, `skeleton-fixture-${sketch.getNodes().length}n-${sketch.getAllEdges().length}e.json`);
  }

  private loadSketchFixture(json: string): void {
    const sketch = this.getCurrentFloorWallSketch();
    if (!sketch) {
      console.warn('Load skeleton fixture: no wall sketch on the current floor');
      return;
    }
    applyFixtureJsonToWallSketch(sketch, json);
  }

  // ─── Commit ─────────────────────────────────────────────────────────

  /**
   * Enter in suggestion mode: resolve the outer-wall components from the
   * room selection and write them into the floor's wall sketch as merged
   * `role:'OUTLINE'` ring edges — then, unless "outer wall only" is on,
   * generate and persist the wall skeleton from the same components. All
   * connected components land in ONE sketch (its wall regions hold every
   * component); no separate OUTLINE annotation is created anymore.
   */
  protected override commit(): void {
    const result = this.commitOutlines();
    if (!result) return;

    const components = result.components;
    const rings: OutlineRingComponent[] = components.map((c) => ({
      outerRing: c.outerRing.map((p) => ({ x: p.x, y: p.y, z: result.floorHeight })),
      holes: c.holes.map((h) => h.map((p) => ({ x: p.x, y: p.y, z: result.floorHeight }))),
    }));

    if (this.outlineOnly) {
      // "Outer wall only": just the merged outline rings, no wall skeleton.
      writeOutlineRingsToSketch(this.viewer, result.floorGroupIdentifier, result.floorHeight, rings);
      this.dispatchPhase();
      return;
    }

    const wallRegions: WallRegionData[] = components.map((c) => ({
      outerRing: c.outerRing.map((p) => ({ x: p.x, y: p.y })),
      holes: c.holes.map((h) => h.map((p) => ({ x: p.x, y: p.y }))),
    }));
    // Reuse the settled preview result when it matches the committed inputs.
    const cached = this.cachedPreview && this.cachedPreview.key === this.previewKey(result.rooms) ? this.cachedPreview.skeleton : undefined;

    // Suggestion previews are done — the sketch is about to become the real
    // geometry (handleWallSketchAdded re-runs refreshPreview + phase once the
    // scene object lands).
    this.cancelSketchPreview();
    this.previewRenderer.clear();

    void generateAndPersistWallSketch(this.viewer, result.floorGroupIdentifier, result.floorHeight, {
      rooms: result.rooms,
      wallRegion: components,
      outlineIdentifier: null,
      processedSkeleton: cached,
      wallRegions,
      outlineRings: rings,
    })
      .then(() => this.dispatchPhase())
      .catch((err) => console.error('Wall sketch generation failed:', err));
  }
}
